"use client";

import { useState } from "react";
import { Image } from "@/scott/ui/elements/image/image";
import { IconVideoPlay } from "@/scott/ui/icons/icon-video-play";
import { Container } from "@/scott/ui/layout/grid";
import type { ProductDetailIntroductionVideo } from "../product-detail-page.adapter";

export interface ProductIntroductionVideoSectionProps {
  video?: ProductDetailIntroductionVideo;
  /** Localized "Play video"; the button is announced as "<label>: <title>". */
  playLabel: string;
}

/** Asks the player to start right away, so one click both loads and plays. */
export function withAutoplay(src: string): string {
  try {
    const url = new URL(src);
    url.searchParams.set("autoplay", "1");
    return url.toString();
  } catch {
    return src;
  }
}

/**
 * Shows a still with a play button and loads the provider's player only when
 * the shopper clicks it. The YouTube embed alone is close to 1 MB (about 850 KB
 * of JavaScript), and `loading="lazy"` does not avoid it: Chrome starts lazy
 * iframes well before they scroll into view.
 */
export function ProductIntroductionVideoSection(
  props: Readonly<ProductIntroductionVideoSectionProps>
) {
  const [isPlaying, setIsPlaying] = useState(false);
  const video = props.video;

  if (!video?.src) {
    return null;
  }

  const title = video.title ?? "Product introduction video";

  return (
    <Container paddingBottom="medium" paddingTop="none">
      <div className="relative aspect-video w-full overflow-hidden bg-black">
        {isPlaying ? (
          <iframe
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            className="h-full w-full border-0"
            src={withAutoplay(video.src)}
            title={title}
          />
        ) : (
          <button
            aria-label={`${props.playLabel}: ${title}`}
            className="group absolute inset-0 flex cursor-pointer items-center justify-center"
            onClick={() => setIsPlaying(true)}
            type="button"
          >
            {video.posterSrc && (
              <span className="absolute inset-0">
                <Image
                  alt=""
                  aspectRatio="16:9"
                  decorative
                  fill
                  loading="lazy"
                  objectFit="cover"
                  sizes="(min-width: 1440px) 1440px, 100vw"
                  src={video.posterSrc}
                />
              </span>
            )}
            <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-black/60 transition-colors group-hover:bg-black/80 group-focus-visible:bg-black/80">
              <IconVideoPlay className="ml-1 h-7 w-7" />
            </span>
          </button>
        )}
      </div>
    </Container>
  );
}
